import { useState, useEffect } from 'react';
import TradingChartModal from './TradingChartModal';

function LivePricesWidget() {
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [prices, setPrices] = useState({
    XAUUSD: { price: 2635.40, change: 12.50, high: 2642.30, low: 2618.90 },
    NAS100: { price: 16245.30, change: -45.20, high: 16298.50, low: 16187.40 },
    GBPJPY: { price: 193.56, change: 0.84, high: 194.12, low: 192.88 },
    SP500: { price: 4567.80, change: 18.30, high: 4578.90, low: 4542.10 }
  });

  const instruments = [
    { 
      symbol: 'XAUUSD', 
      name: 'Gold', 
      fullName: 'XAU/USD',
      icon: '💰',
      color: 'from-yellow-600 to-yellow-800'
    },
    { 
      symbol: 'NAS100', 
      name: 'NASDAQ 100', 
      fullName: 'US Tech 100',
      icon: '📈',
      color: 'from-blue-600 to-blue-800'
    },
    { 
      symbol: 'GBPJPY', 
      name: 'GBP/JPY', 
      fullName: 'British Pound / Japanese Yen',
      icon: '💷',
      color: 'from-purple-600 to-purple-800'
    },
    { 
      symbol: 'SP500', 
      name: 'S&P 500', 
      fullName: 'US 500',
      icon: '📊',
      color: 'from-green-600 to-green-800'
    }
  ];

  // Simulate live price updates
  useEffect(() => {
    const updatePrices = () => {
      setPrices(prev => {
        const newPrices = { ...prev };
        
        Object.keys(newPrices).forEach(symbol => {
          const volatility = {
            XAUUSD: 0.3,
            NAS100: 3,
            GBPJPY: 0.03,
            SP500: 1.5
          };

          const change = (Math.random() - 0.5) * 2 * volatility[symbol];
          const newPrice = newPrices[symbol].price + change;
          
          // Update high/low
          const newHigh = Math.max(newPrices[symbol].high, newPrice);
          const newLow = Math.min(newPrices[symbol].low, newPrice);

          newPrices[symbol] = {
            ...newPrices[symbol],
            price: parseFloat(newPrice.toFixed(2)),
            change: parseFloat((newPrices[symbol].change + change).toFixed(2)),
            high: parseFloat(newHigh.toFixed(2)),
            low: parseFloat(newLow.toFixed(2))
          };
        });

        return newPrices;
      });
    };

    const interval = setInterval(updatePrices, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-16 px-6 max-w-7xl mx-auto">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 mb-4">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF7F] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#00FF7F]"></span>
          </span>
          <span className="text-sm font-light text-[#00FF7F] uppercase tracking-wider">Live Market Prices</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-3">
          Real-Time <span className="text-[#00FF7F]">Trading Prices</span>
        </h2>
        <p className="text-gray-400 font-light">
          Track the markets Kasango AI Pro trades 24/7
        </p>
      </div>

      {/* Price Cards Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {instruments.map((instrument) => {
          const data = prices[instrument.symbol];
          const isPositive = data.change >= 0;
          const changePercent = ((data.change / data.price) * 100).toFixed(2);

          return (
            <button
              key={instrument.symbol}
              onClick={() => {
                setSelectedInstrument(instrument);
                setIsModalOpen(true);
              }}
              className={`relative bg-gradient-to-br ${instrument.color} rounded-2xl p-6 border border-zinc-700 hover:scale-105 transition-transform duration-300 overflow-hidden cursor-pointer group w-full text-left`}
            >
              {/* Background Icon */}
              <div className="absolute top-4 right-4 text-6xl opacity-10">
                {instrument.icon}
              </div>

              {/* Content */}
              <div className="relative z-10">
                {/* Symbol & Name */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">{instrument.icon}</span>
                  <div>
                    <h3 className="text-lg font-bold text-white">{instrument.name}</h3>
                    <p className="text-xs text-gray-300">{instrument.fullName}</p>
                  </div>
                </div>

                {/* Current Price */}
                <div className="mb-4">
                  <div className="text-3xl font-bold text-white mb-1">
                    {data.price.toFixed(2)}
                  </div>
                  <div className={`flex items-center gap-2 text-sm font-medium ${
                    isPositive ? 'text-green-300' : 'text-red-300'
                  }`}>
                    <span>{isPositive ? '▲' : '▼'}</span>
                    <span>{isPositive ? '+' : ''}{data.change.toFixed(2)}</span>
                    <span>({isPositive ? '+' : ''}{changePercent}%)</span>
                  </div>
                </div>

                {/* High/Low */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-black/20 rounded-lg p-2">
                    <div className="text-gray-300 mb-1">High</div>
                    <div className="text-white font-bold">{data.high.toFixed(2)}</div>
                  </div>
                  <div className="bg-black/20 rounded-lg p-2">
                    <div className="text-gray-300 mb-1">Low</div>
                    <div className="text-white font-bold">{data.low.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              {/* Click Indicator */}
              <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full text-xs text-white flex items-center gap-1">
                  <span>View Chart</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* CTA */}
      <div className="text-center mt-12">
        <a
          href="https://app.winnersgroundfx.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-8 py-4 bg-[#00FF7F] hover:bg-[#00DD6F] text-black font-bold rounded-xl transition-all hover:scale-105"
        >
          <span>Start Trading with Kasango AI Pro</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </a>
        <p className="text-xs text-gray-500 mt-4">
          * Prices updated every 3 seconds. For actual trading, visit our platform.
        </p>
      </div>

      {/* Trading Chart Modal */}
      <TradingChartModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        instrument={selectedInstrument}
      />
    </section>
  );
}

export default LivePricesWidget;
